import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { ExperimentResult } from '../../types/experiment.types';
import UpliftBadge from './UpliftBadge';
import ConfidenceBadge from './ConfidenceBadge';

interface ExperimentResultsTableProps {
  results: ExperimentResult[];
  loading?: boolean;
  compact?: boolean;
}

const ExperimentResultsTable: React.FC<ExperimentResultsTableProps> = ({ 
  results, 
  loading = false,
  compact = false 
}) => {
  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading results...</Typography>
      </Box>
    );
  }

  if (!results || results.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="textSecondary">No results available</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table size={compact ? 'small' : 'medium'}>
        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Engine</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                Uplift
                <Tooltip title="Percentage change with 95% confidence interval">
                  <Info fontSize="small" />
                </Tooltip>
              </Box>
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                P-Value
                <Tooltip title="Statistical significance test">
                  <Info fontSize="small" />
                </Tooltip>
              </Box>
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Confidence</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Method</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                Sample Size
                <Tooltip title="Number of observations in analysis">
                  <Info fontSize="small" />
                </Tooltip>
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {results.map((result, idx) => (
            <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {result.metric}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{result.engine}</Typography>
              </TableCell>
              <TableCell align="right">
                <UpliftBadge
                  uplift={result.uplift}
                  ciLow={result.ciLow}
                  ciHigh={result.ciHigh}
                  size="small"
                />
              </TableCell>
              <TableCell align="center">
                <Typography 
                  variant="body2"
                  sx={{
                    color: result.pValue < 0.05 ? '#2e7d32' : '#d32f2f',
                    fontWeight: 600
                  }}
                >
                  {result.pValue.toFixed(4)}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <ConfidenceBadge level={result.confidenceLevel} size="small" />
              </TableCell>
              <TableCell align="center">
                <Typography variant="caption" sx={{ backgroundColor: '#e3f2fd', px: 1, py: 0.5, borderRadius: 1 }}>
                  {result.method}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">
                  {result.sampleSize.toLocaleString()}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ExperimentResultsTable;
